import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Stack,
  InputAdornment,
  Paper,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import planService from '../../services/planService';
import { PERMISSION_LOOKUP } from '../../constants/permissionCatalog';

const getPermission = (slug, overrides = {}) => ({
  slug,
  name: PERMISSION_LOOKUP[slug].name,
  is_allowed: true,
  limit: PERMISSION_LOOKUP[slug].defaultLimit,
  period: PERMISSION_LOOKUP[slug].defaultPeriod,
  ...overrides
});

const getPermissionLimit = (permissions, slug, fallback) => {
  const permission = permissions?.find((item) => item.slug === slug);
  return permission?.limit ?? fallback;
};

const AddEditPlan = ({ plan, editMode, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_days: '',
    is_active: true,
    contactLimit: '',
    officeAssistedProfiles: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editMode && plan) {
      const permissions = Array.isArray(plan.permissions) ? plan.permissions : [];
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        price: plan.price ?? '',
        duration_days: plan.duration_days ?? '',
        is_active: plan.is_active !== undefined ? plan.is_active : true,
        contactLimit: plan.contact_view_limit ?? getPermissionLimit(permissions, 'view_contact_details', 0),
        officeAssistedProfiles: plan.office_assisted_profiles ?? getPermissionLimit(permissions, 'office_assisted_profiles', 0)
      });
      setError('');
      return;
    }

    setFormData({
      name: '',
      description: '',
      price: '',
      duration_days: '',
      is_active: true,
      contactLimit: '',
      officeAssistedProfiles: ''
    });
    setError('');
  }, [editMode, plan]);

  const permissionsPreview = useMemo(() => {
    const contactLimit = Number(formData.contactLimit) || 0;
    const officeProfiles = Number(formData.officeAssistedProfiles) || 0;
    const permissions = [
      getPermission('view_contact_details', {
        limit: contactLimit,
        period: 'total',
        is_allowed: contactLimit > 0
      }),
      getPermission('chat_messages', {
        limit: null,
        period: 'total',
        is_allowed: true
      })
    ];

    if (officeProfiles > 0) {
      permissions.push(getPermission('office_assisted_profiles', {
        limit: officeProfiles,
        period: 'total',
        is_allowed: true
      }));
    }

    return permissions;
  }, [formData.contactLimit, formData.officeAssistedProfiles]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Plan name is required');
      return false;
    }

    if (!formData.price || Number(formData.price) <= 0) {
      setError('Valid plan price is required');
      return false;
    }

    if (!formData.duration_days || Number(formData.duration_days) <= 0) {
      setError('Valid plan validity is required');
      return false;
    }

    if (Number(formData.contactLimit || 0) < 0) {
      setError('Contact view limit cannot be negative');
      return false;
    }

    if (Number(formData.officeAssistedProfiles || 0) < 0) {
      setError('Office assisted profiles cannot be negative');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: Number(formData.price),
        duration_days: Number(formData.duration_days),
        is_active: formData.is_active,
        contact_view_limit: Number(formData.contactLimit) || 0,
        unlimited_chat: true,
        office_assisted_profiles: Number(formData.officeAssistedProfiles) || 0,
        permissions: permissionsPreview
      };

      if (editMode) {
        await planService.updatePlan(plan._id, submitData);
      } else {
        await planService.createPlan(submitData);
      }

      onSave();
    } catch (error) {
      console.error('Error saving plan:', error);
      setError(error.response?.data?.message || 'Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  const contactLimit = Number(formData.contactLimit) || 0;
  const officeProfiles = Number(formData.officeAssistedProfiles) || 0;
  const validityDays = Number(formData.duration_days) || 0;

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Membership Plan</Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the exact plan values. The client website and user access will follow these saved values.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Plan Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Price"
            type="number"
            value={formData.price}
            onChange={(e) => handleInputChange('price', e.target.value)}
            required
            inputProps={{ min: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Validity (Days)"
            type="number"
            value={formData.duration_days}
            onChange={(e) => handleInputChange('duration_days', e.target.value)}
            required
            inputProps={{ min: 1 }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Plan Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Write the plan description shown on the website"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Contact Details View Limit"
            type="number"
            value={formData.contactLimit}
            onChange={(e) => handleInputChange('contactLimit', e.target.value)}
            helperText="Leave 0/blank if contact details should not be included."
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Office Suggested Profiles"
            type="number"
            value={formData.officeAssistedProfiles}
            onChange={(e) => handleInputChange('officeAssistedProfiles', e.target.value)}
            helperText="Leave 0/blank if personalized office profile suggestions are not included."
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={formData.is_active} onChange={(e) => handleInputChange('is_active', e.target.checked)} />}
            label="Active"
          />
        </Grid>

        <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Website Benefit Preview</Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {contactLimit > 0 && <Chip label={`Contact details: ${contactLimit} profiles`} color="primary" variant="outlined" />}
              <Chip label="Unlimited chat" color="success" variant="outlined" />
              {validityDays > 0 && <Chip label={`${validityDays} days validity`} variant="outlined" />}
              {officeProfiles > 0 && <Chip label={`Office suggestions: ${officeProfiles} profiles`} color="warning" variant="outlined" />}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={loading}>
              {loading ? 'Saving...' : (editMode ? 'Update Plan' : 'Create Plan')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddEditPlan;